<template>
  <section class="article-card">
    <NuxtLink
      class="article-card__link"
      :class="{
        'article-card__link--no-domain' : !props.data.domain?.color?.name,
      }"
      :href="`/article/${joinSlugId(props.data)}/`"
      :data-domain="props.data.domain?.color?.name"
      @click="onClicked"
    >
      <div class="article-card__image-focal-container">
        <AppImageFocal
          v-if="props.data.image"
          :data="props.data.image"
          :focal-point="1"
          :renditions="renditions"
          class="article-card__image"
          aria-hidden="true"
        />
      </div>
      <div
        class="article-card__content"
      >
        <ul
          v-if="props.data.belongs_to[0]?.item || props.data.reading_time"
          class="article-card__list"
        >
          <li>
            <AppTagText
              v-if="props.data.belongs_to[0]?.item"
              :title="props.data.belongs_to[0].item.title"
            />
          </li>
        </ul>
        <component :is="props.heading" ref="displayTitle"
            class="article-card__display-title"
            :class="{
              'article-card__display-title--large' : variant === 'large',
            }"
            v-html="props.data.title"
        >
        </component>
      </div>
    </NuxtLink>
  </section>
</template>

<script setup lang="ts">
import {onUnmounted, ref} from 'vue'
import {TArticlePage} from '~/types/page-types'
import {TArticleCard} from '~/types/card-types'
import {useAnimatedTitleStore} from '~/store/animated-title'
import {useHeadingContentAware} from '~/composables/useHeadingContentAware'
import {joinSlugId} from "~/utils/slug-id";

const renditions = [
  { width: 308, height: 308, breakpoint: 'xs' },
]

interface IProps {
  heading?: 'h2' | 'h3',
  data: TArticlePage | TArticleCard,
}

const props = withDefaults(defineProps<IProps>(), {
  heading: 'h3'
})

const displayTitle = ref()
const animatedTitleStore = useAnimatedTitleStore()
const variant = useHeadingContentAware(props.data.title, 6)

const onClicked = () => {
  animatedTitleStore.dimensions = displayTitle.value.getBoundingClientRect()
  animatedTitleStore.title = displayTitle.value.innerHTML
  animatedTitleStore.classes = displayTitle.value.classList.toString()
  animatedTitleStore.domain = ''
  animatedTitleStore.animate = true
}
onUnmounted(() => {
  animatedTitleStore.clear()
})
</script>

<style lang="postcss">
.article-card {
  &__link {
    display: grid;
    aspect-ratio: 1 / 1;
    overflow: clip;
    border-radius: 1rem;
    border-width: 3px;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    @mixin app-box-shadow-with-hover;
    background-color: var(--color-blue-aqua);

    &--no-domain {
      border-color: var(--color-blue-aqua);
      color: var(--color-blue-aqua);
    }

    @media (--lg) {
      border-radius: 1.5rem;
    }

    &[data-domain="violet"] {
      border-color: var(--color-violet);
      background-color: var(--color-violet);
      color: var(--color-violet);
    }

    &[data-domain="yellow"] {
      border-color: var(--color-yellow);
      background-color: var(--color-yellow);
      color: var(--color-yellow);
    }

    &[data-domain="red"] {
      border-color: var(--color-red);
      background-color: var(--color-red);
      color: var(--color-red);
    }
  }

  &__image-focal-container {
    position: relative;
    grid-column: 1 / -1;
    grid-row-start: 1;
  }

  &__image {
    display: flex;
    width: 100%;
    flex: 1 1 auto;
    justify-content: stretch;
    overflow: clip;
  }

  &__content {
    z-index: 20;
    grid-column: 1 / -1;
    grid-row-start: 1;
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 0.625rem;
    padding: 1rem;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(360deg, rgba(0, 0, 0, 0.80) 11.38%, rgba(0, 0, 0, 0.00) 100%);

    @media (--lg) {
      padding: 1.5rem;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;

    @media (--lg) {
      gap: 0.5rem;
    }
  }

  &__display-title {
    pointer-events: none;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    hyphens: auto;
    text-align: center;
    font-family: Midnight, sans-serif;
    font-size: 16px;
    line-height: 1.2;
    color: #fff;
    @mixin hyphenation-specs;

    &--large {
      text-transform: uppercase;
    }
  }
}

.page-leave-active {
  .article-card .article-card__display-title {
    opacity: 0;
  }
}
</style>
